import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, ShoppingBag, LogOut } from "lucide-react";
import { ReferralHub } from "@/components/referrals/ReferralHub";

/**
 * Customer "My Account" page — email, saved address, and a hard delete
 * button. Deletion wipes profile + saved address + active sessions; orders
 * are kept but the email/name/mobile fields on them are nulled/anonymized
 * server-side.
 */
export default function Account() {
  const { user, loading: authLoading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/account", { replace: true }); return; }
    api.get("/auth/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, [user, authLoading, navigate]);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account");
      setUser(null);
      toast.success("Your account has been deleted.");
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to delete account.";
      toast.error(detail);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (authLoading || !user) {
    return <main className="min-h-screen flex items-center justify-center text-espresso-light label-xs">Loading…</main>;
  }

  const addr = profile?.saved_address;

  return (
    <main data-testid="account-page" className="min-h-screen bg-ivory">
      <div className="mx-auto max-w-[900px] px-4 pb-28 pt-10 md:px-10 md:py-20">
        <div className="label-xs text-espresso-light">Account</div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-espresso">Hi, {user.name || (user.email || "").split("@")[0]}.</h1>
        <p className="mt-3 text-sm text-espresso-light">Manage your profile, addresses, and account.</p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="border border-line p-6 md:p-8 bg-ivory">
            <div className="label-xs text-espresso-light">Profile</div>
            <div className="mt-4">
              <div className="text-espresso-light label-xs">Email</div>
              <div className="mt-1 text-espresso" data-testid="account-email">{user.email}</div>
            </div>
            {profile?.phone && (
              <div className="mt-4">
                <div className="text-espresso-light label-xs">Phone</div>
                <div className="mt-1 text-espresso">{profile.phone}</div>
              </div>
            )}
          </div>

          <div className="border border-line p-6 md:p-8 bg-ivory">
            <div className="label-xs text-espresso-light">Saved delivery address</div>
            {addr?.address_line1 ? (
              <div className="mt-4 text-sm text-espresso leading-relaxed" data-testid="account-address">
                {addr.full_name && <div>{addr.full_name}</div>}
                {addr.address_line1 && <div>{addr.address_line1}</div>}
                {addr.address_line2 && <div>{addr.address_line2}</div>}
                {[addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).length > 0 && (
                  <div>{[addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</div>
                )}
              </div>
            ) : (
              <p className="mt-4 text-sm text-espresso-light">No saved address yet — placing an order will save one for you.</p>
            )}
          </div>
        </div>

        <ReferralHub />

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/my-orders" data-testid="account-orders-link" className="inline-flex w-full items-center justify-center gap-2 border border-espresso px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-espresso transition-colors duration-200 hover:bg-espresso hover:text-ivory sm:w-auto">
            <ShoppingBag size={14} strokeWidth={1.5} /> My orders
          </Link>
          <button onClick={doLogout} data-testid="account-logout-btn" className="inline-flex w-full items-center justify-center gap-2 border border-espresso px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-espresso transition-colors duration-200 hover:bg-espresso hover:text-ivory sm:w-auto">
            <LogOut size={14} strokeWidth={1.5} /> Log out
          </button>
        </div>

        {/* Danger zone */}
        <div className="mt-12 border border-red-200 bg-red-50/40 p-6 md:p-8">
          <div className="label-xs text-red-600 tracking-[0.2em]">Danger zone</div>
          <h2 className="mt-2 font-serif text-2xl text-espresso">Delete my account</h2>
          <p className="mt-2 text-sm text-espresso-light max-w-lg">
            This permanently removes your profile and saved address. Your order history is preserved for the boutique's records but is anonymized — your name and email are stripped.
          </p>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              data-testid="account-delete-btn"
              className="mt-5 border border-red-500 text-red-700 px-5 py-3 uppercase tracking-[0.2em] text-[10px] hover:bg-red-500 hover:text-ivory transition-colors inline-flex items-center gap-2"
            >
              <Trash2 size={14} strokeWidth={1.5} /> Delete account
            </button>
          ) : (
            <div className="mt-5 space-y-3" data-testid="account-delete-confirm">
              <p className="text-sm text-espresso">Are you sure? This can't be undone.</p>
              <div className="flex gap-3">
                <button onClick={deleteAccount} disabled={busy} data-testid="account-delete-confirm-btn" className="bg-red-600 text-ivory px-5 py-3 uppercase tracking-[0.2em] text-[10px] hover:bg-red-700 disabled:opacity-40">
                  {busy ? "Deleting…" : "Yes, delete permanently"}
                </button>
                <button onClick={() => setConfirming(false)} className="border border-espresso text-espresso px-5 py-3 uppercase tracking-[0.2em] text-[10px]">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
